import { useEffect, useRef, useState } from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import { ImageBlockToolbar } from './ImageBlockToolbar';
import styles from './styles.module.css';

type Corner = 'nw' | 'ne' | 'sw' | 'se';

const MIN_SIZE = 40;
const DRAG_THRESHOLD = 3; // px — mover menos que isso não inicia drag

function toNumberOr(value: unknown, fallback: number): number {
  if (typeof value === 'number' && isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = parseFloat(value);
    if (isFinite(n)) return n;
  }
  return fallback;
}

// Encontra o `.page` real (marcado com data-page="true") para usar como referencial
// visual da folha. NÃO é o offsetParent CSS — o ProseMirror tem position:relative e
// é o offsetParent, mas seu clientWidth é a área de conteúdo, não a folha.
function findPageAncestor(el: HTMLElement | null): HTMLElement | null {
  return el?.closest('[data-page="true"]') as HTMLElement | null;
}

export function ImageBlockView({ node, updateAttributes, deleteNode, selected, editor }: NodeViewProps) {
  const { src, alt, width, height, align, float, posX, posY } = node.attrs as {
    src?: string;
    alt?: string;
    width?: number | string | null;
    height?: number | string | null;
    align?: 'left' | 'center' | 'right' | null;
    float?: 'left' | 'right' | null;
    posX?: number | null;
    posY?: number | null;
  };

  const imgRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [flipped, setFlipped] = useState(false);

  const isEditable = editor.isEditable;
  const showHandles = isEditable && selected;
  const isFreePositioned = posX != null && posY != null;

  // Detecta se a toolbar precisa flipar pra baixo
  useEffect(() => {
    if (!showHandles || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const editorRect = editor.view.dom.getBoundingClientRect();
    setFlipped(rect.top - editorRect.top < 50);
  }, [showHandles, editor, posX, posY]);

  // Cleanup de listeners se desmontar mid-drag
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  // ─── Resize via alças ───
  function startResize(corner: Corner, e: React.MouseEvent) {
    if (!imgRef.current) return;
    e.preventDefault();
    e.stopPropagation();

    const startW = imgRef.current.offsetWidth;
    const startH = imgRef.current.offsetHeight;
    const ratio = startH > 0 ? startW / startH : 1;
    const startX = e.clientX;
    const startY = e.clientY;

    const onMove = (ev: MouseEvent) => {
      if (!imgRef.current) return;
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const horizontalSign = corner === 'ne' || corner === 'se' ? 1 : -1;
      const verticalSign = corner === 'sw' || corner === 'se' ? 1 : -1;
      const candidateByX = startW + horizontalSign * dx;
      const candidateByY = (startH + verticalSign * dy) * ratio;
      let newW = Math.max(candidateByX, candidateByY);
      newW = Math.max(MIN_SIZE, Math.round(newW));
      const newH = Math.max(MIN_SIZE, Math.round(newW / ratio));
      imgRef.current.style.width = `${newW}px`;
      imgRef.current.style.height = `${newH}px`;
    };

    const onUp = () => {
      if (imgRef.current) {
        const finalW = imgRef.current.offsetWidth;
        const finalH = imgRef.current.offsetHeight;
        updateAttributes({ width: finalW, height: finalH });
      }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mouseleave', onUp);
      cleanupRef.current = null;
    };

    cleanupRef.current = onUp;

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mouseleave', onUp);
  }

  // ─── Drag livre da imagem ───
  function startDragMove(e: React.MouseEvent) {
    if (!isEditable || !containerRef.current) return;
    if (!selected) return;
    e.preventDefault();
    e.stopPropagation();

    const page = findPageAncestor(containerRef.current);
    const wrapperEl = containerRef.current;
    if (!page || !wrapperEl) return;

    const pageRect = page.getBoundingClientRect();
    const wrapperRect = wrapperEl.getBoundingClientRect();
    const offsetParentEl = (wrapperEl.offsetParent as HTMLElement | null) || page;
    const offsetParentRect = offsetParentEl.getBoundingClientRect();
    // Offset entre o offsetParent CSS (provavelmente .ProseMirror) e a .page real.
    // É isso que precisamos compensar quando setamos left/top.
    const offsetFromPageX = offsetParentRect.left - pageRect.left;
    const offsetFromPageY = offsetParentRect.top - pageRect.top;

    const initialX = wrapperRect.left - pageRect.left;
    const initialY = wrapperRect.top - pageRect.top;
    const startMouseX = e.clientX;
    const startMouseY = e.clientY;
    const imgW = imgRef.current?.offsetWidth || wrapperRect.width;
    const imgH = imgRef.current?.offsetHeight || wrapperRect.height;
    const pageW = pageRect.width;
    const pageH = pageRect.height;
    let moved = false;

    // Clamp nas bordas da página (folha inteira, ignora as margens).
    // posX/posY são armazenados em coordenadas relativas à .page real.
    const clampX = (x: number) => {
      const max = pageW - imgW;
      if (max < 0) return 0;
      return Math.max(0, Math.min(max, x));
    };
    const clampY = (y: number) => {
      const max = pageH - imgH;
      if (max < 0) return 0;
      return Math.max(0, Math.min(max, y));
    };

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startMouseX;
      const dy = ev.clientY - startMouseY;
      if (!moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      moved = true;

      const newX = clampX(Math.round(initialX + dx));
      const newY = clampY(Math.round(initialY + dy));

      // left/top são relativos ao offsetParent CSS, não à .page. Subtrai o offset
      // para que o resultado visual fique em (newX, newY) relativo à .page.
      wrapperEl.style.position = 'absolute';
      wrapperEl.style.left = `${newX - offsetFromPageX}px`;
      wrapperEl.style.top = `${newY - offsetFromPageY}px`;
      wrapperEl.style.float = 'none';
      wrapperEl.style.width = `${imgW}px`;
      wrapperEl.style.height = `${imgH}px`;
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mouseleave', onUp);
      cleanupRef.current = null;

      if (moved) {
        const rect = wrapperEl.getBoundingClientRect();
        const pr = page.getBoundingClientRect();
        const finalX = clampX(Math.round(rect.left - pr.left));
        const finalY = clampY(Math.round(rect.top - pr.top));
        updateAttributes({ posX: finalX, posY: finalY, align: null, float: null });
      }
    };

    cleanupRef.current = onUp;

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mouseleave', onUp);
  }

  const handleAlign = (value: 'left' | 'center' | 'right') => {
    updateAttributes({ align: value, float: null, posX: null, posY: null });
  };
  const handleFloat = (value: 'left' | 'right') => {
    updateAttributes({ float: value, align: null, posX: null, posY: null });
  };

  const styleWidth =
    width != null && width !== '' ? toNumberOr(width, 0) || undefined : undefined;
  const styleHeight =
    height != null && height !== '' ? toNumberOr(height, 0) || undefined : undefined;

  // Quando free-positioned, left/top precisam ser ajustados pelo offset entre o
  // offsetParent CSS (ProseMirror, com position:relative) e a .page real. Isso é
  // feito em useEffect porque precisa medir o DOM.
  useEffect(() => {
    const wrapperEl = containerRef.current;
    if (!wrapperEl) return;

    if (!isFreePositioned) {
      // Limpa inline-styles de drag se sair do modo livre
      wrapperEl.style.position = '';
      wrapperEl.style.left = '';
      wrapperEl.style.top = '';
      wrapperEl.style.float = '';
      wrapperEl.style.margin = '';
      wrapperEl.style.width = '';
      wrapperEl.style.height = '';
      wrapperEl.style.zIndex = '';
      return;
    }

    const page = findPageAncestor(wrapperEl);
    const offsetParentEl = (wrapperEl.offsetParent as HTMLElement | null) || page;
    if (!page || !offsetParentEl) return;

    const pageRect = page.getBoundingClientRect();
    const opRect = offsetParentEl.getBoundingClientRect();
    const offsetX = opRect.left - pageRect.left;
    const offsetY = opRect.top - pageRect.top;

    wrapperEl.style.position = 'absolute';
    wrapperEl.style.left = `${(posX as number) - offsetX}px`;
    wrapperEl.style.top = `${(posY as number) - offsetY}px`;
    wrapperEl.style.float = 'none';
    wrapperEl.style.margin = '0';
    wrapperEl.style.zIndex = '1';
    if (styleWidth) wrapperEl.style.width = `${styleWidth}px`;
    if (styleHeight) wrapperEl.style.height = `${styleHeight}px`;
  }, [isFreePositioned, posX, posY, styleWidth, styleHeight]);

  return (
    <NodeViewWrapper
      as="div"
      ref={containerRef}
      className={styles.container}
      data-align={align || undefined}
      data-float={float || undefined}
      data-free={isFreePositioned ? 'true' : undefined}
    >
      <span
        className={`${styles.imageWrapper} ${showHandles ? styles.selected : ''} ${
          isEditable && selected ? styles.draggable : ''
        }`}
        onMouseDown={startDragMove}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt || ''}
          style={{
            width: styleWidth ? `${styleWidth}px` : undefined,
            height: styleHeight ? `${styleHeight}px` : undefined,
          }}
          draggable={false}
        />
        {showHandles && (
          <>
            <div
              className={`${styles.handle} ${styles.handleNw}`}
              onMouseDown={(e) => startResize('nw', e)}
            />
            <div
              className={`${styles.handle} ${styles.handleNe}`}
              onMouseDown={(e) => startResize('ne', e)}
            />
            <div
              className={`${styles.handle} ${styles.handleSw}`}
              onMouseDown={(e) => startResize('sw', e)}
            />
            <div
              className={`${styles.handle} ${styles.handleSe}`}
              onMouseDown={(e) => startResize('se', e)}
            />
            <ImageBlockToolbar
              align={align || null}
              float={float || null}
              onAlignChange={handleAlign}
              onFloatChange={handleFloat}
              onDelete={() => deleteNode()}
              flipped={flipped}
            />
          </>
        )}
      </span>
    </NodeViewWrapper>
  );
}
